// src/pages/PrivacyPolicyPage.js

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import Footer from '../components/Footer'; 
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-page">
      <PublicNavbar />
      
      {/* Hero Section */}
      <header className="privacy-hero">
        <div className="privacy-hero-content">
          <h1>Privacy Policy</h1>
          <p>Transparency, Security, and Trust</p>
        </div>
      </header>

      {/* Main page content */}
      <div className="privacy-main-content">
        <main>
          <section className="privacy-section">
            <p>Last Updated: December 2025</p>
            <p>
              At Refuge AI, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use the Refuge AI mobile application and website.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information We Collect</h2>
            
            <h3>1. Account Registration Information</h3>
            <p>
              To provide you with access to our curated resources and community features, we ask you to register for an account. During this process, we collect the following personal information:
            </p>
            <ul>
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Phone Number</li>
              <li>Mailing Address</li>
            </ul>
            <p>
              This information is securely stored in our database (hosted by Google Firebase) and is used solely to manage your account and verify user authenticity.
            </p>

            <h3>2. User-Generated Content (Resumes)</h3>
            <p>
              Our app allows you to create and edit a professional resume to assist in your job search. <strong>Your resume data is stored locally on your own device.</strong> We do not automatically upload your resume content to our servers unless you explicitly choose to share it through specific features. You retain full control over this document.
            </p>

            <h3>3. Location Data Map Functionality (Precise Location)</h3>
            <p>
              Our application requests access to your device's precise location. We use this strictly to display your current position on the in-app map relative to our resources and to facilitate navigation handoffs to third-party map applications (e.g., Google Maps). <b>We do not store your precise location coordinates on our servers.</b> 
            </p>
            <p>Approximate Location: We may collect anonymous, aggregate data regarding the general region (e.g., US State) where the app is utilized. This helps us understand community needs and allocate resources effectively. This data is not linked to your personal identity.</p>
            
            <h3>4. Reviews and Feedback</h3>
            <p>
              We value your input and often use feedback to improve the app for all users. If you submit a positive review or testimonial (such as a 5-star rating) directly to the company, we may share this feedback to help others learn about the app and how it can help them.
            </p>
            <p>
              <strong>Protecting Your Identity:</strong> When sharing these reviews publicly or in marketing materials, we will never share your full personal information. We will strictly limit identification to your <strong>First Name and Last Initial</strong> (e.g., "Bob C. in Arizona said: 'This app is great, it has helped me a lot'").
            </p>

            <h3>5. Data Retention and Account Deletion</h3>
            <p>
              We retain your personal information only for as long as your account is active. You have the right to delete your account and all associated data at any time. You can delete your account directly within the app settings, or by contacting us via our website’s Contact page with the subject "Account Deletion Request." Upon request, all your personal data (Name, Email, Address, Phone) will be permanently removed from our active database.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Integration with Device Features</h2>
            <p>
              To make the app helpful and easy to use, Refuge AI integrates with other applications installed on your device. With your permission, our app may trigger actions in:
            </p>
            <ul>
              <li><strong>Google Maps (or your default map app):</strong> To provide navigation to resources.</li>
              <li><strong>Email Client:</strong> To help you easily send correspondence.</li>
              <li><strong>Messages/SMS:</strong> To facilitate communication.</li>
            </ul>
            <p>
              When you click these buttons, you are leaving the Refuge AI app. We are not responsible for the data collection practices of these third-party applications.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data Security</h2>
            <p>
              We utilize industry-standard security measures provided by Google Firebase to protect your personal information from unauthorized access, alteration, or destruction. However, please be aware that no method of transmission over the internet or method of electronic storage is 100% secure.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us through our website's <Link to="/contact">Contact page</Link> or send us an email directly at <a href="mailto:support@refugeai.org">support@refugeai.org</a>.
            </p>
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;